import { useGetCollectionQuery } from '../../../services/entities/impl/blfApi/endpoints/collections'; // Adjust path if needed
import { CollectionDetailsNavButton } from '../../collections/components/CollectionDetailsNavButton';
import { useCreateCollectionLabel } from '../collections/hooks/useCreateCollectionLabel';
import DebugInfoSection from '../components/DebugInfoSection'; // Adjust path if needed

type CollectionDebugSectionProps = {
  collectionID: string | undefined;
  itemNumber: number;
  itemsTotal: number;
};

/**
 * A component that displays debug information for a specific collection.
 *
 * We create a separate component for this so we can use useGetCollectionQuery
 * to fetch each unique collectionID individually. This pattern is necessary
 * because in the parent component we may have several collections to display,
 * and a single query wouldn't work for multiple IDs.  This is rare but a scan
 * can be part of multiple collections.
 *
 * @param props.collectionID - The ID of the collection to fetch data for
 * @param props.itemNumber - Number indicating the position of this item in a list
 * @param props.itemsTotal - Number representing the total number of collections
 * @returns A DebugInfoSection component with collection data if available, otherwise null
 */
export const CollectionDebugSection = ({
  collectionID,
  itemNumber,
  itemsTotal,
}: CollectionDebugSectionProps) => {
  const {
    currentData: collection,
    isLoading,
    isFetching,
  } = useGetCollectionQuery(collectionID ?? '', {
    skip: !collectionID,
  });

  const collectionLabel = useCreateCollectionLabel(collection);

  if (!collection) {
    return null;
  }

  return collection ? (
    <DebugInfoSection
      sectionLabel={`Collection${` (${itemNumber + 1} of ${itemsTotal})`}`}
      name={collectionLabel}
      id={collection?.id}
      isLoading={isLoading || isFetching}
      jsonObjectOrArrayOfObjects={collection}
      actionButtonOrElement={
        <CollectionDetailsNavButton collectionId={collection.id} />
      }
    />
  ) : null;
};
